import { appConfig, brandIdentity } from "@/config/app";
import { cn } from "@/lib/utils";
import {
  CLIP_OUTLINE,
  COMPACT_CLIP_LINE,
  COMPACT_CLIP_STROKE,
  COMPACT_SHEET_BACK,
  COMPACT_SHEET_FRONT,
  LETTERS,
  LOCKUP_RATIO,
  LOCKUP_VIEWBOX,
  POTRACE_TRANSFORM,
  SHEET_BACK,
  SHEET_FRONT,
} from "./logo-paths";

type MarkProps = {
  size?: number;
  /** Sur fond foncé : trombone blanc, feuilles inchangées. */
  inverted?: boolean;
  className?: string;
  alt?: string;
};

const customized = () => brandIdentity.logoUrl !== null || brandIdentity.customName;

/** Marque blanche sans logo fourni : l'initiale du nom, sur la couleur de marque. */
function Monogram({ size, inverted }: { size: number; inverted?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center rounded-[28%] font-display font-semibold leading-none",
        inverted ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground",
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.56) }}
    >
      {appConfig.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Pictogramme d'une instance en marque blanche (logo fourni ou monogramme). */
function CustomMark({ size, className, alt }: { size: number; className?: string; alt: string }) {
  if (brandIdentity.logoUrl) {
    return (
      <img
        src={brandIdentity.logoUrl}
        alt={alt}
        width={size}
        height={size}
        className={cn("block shrink-0 object-contain", className)}
      />
    );
  }
  return (
    <span role={alt ? "img" : undefined} aria-label={alt || undefined} className={className}>
      <Monogram size={size} />
    </span>
  );
}

/**
 * Pictogramme Encrelio (trombone devant deux feuilles), version compacte
 * pour les petites tailles. Carré de `size` px ; le trombone prend la
 * couleur du texte (currentColor), les feuilles les rôles `logo-sheet-*`.
 */
export function EncrelioMark({
  size = 28,
  inverted = false,
  className,
  alt = "",
}: MarkProps) {
  if (customized()) return <CustomMark size={size} className={className} alt={alt} />;
  const decorative = alt === "";
  return (
    <svg
      viewBox="-22 0 184 184"
      width={size}
      height={size}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : alt}
      aria-hidden={decorative ? "true" : undefined}
      className={cn("block shrink-0", inverted && "text-white", className)}
    >
      <path className="fill-logo-sheet-back" d={COMPACT_SHEET_BACK} />
      <path className="fill-logo-sheet-front" d={COMPACT_SHEET_FRONT} />
      <path
        d={COMPACT_CLIP_LINE}
        fill="none"
        stroke="currentColor"
        strokeWidth={COMPACT_CLIP_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Nom en texte seul (repli, marque blanche sans logo). */
export function EncrelioWordmark({
  height = 24,
  color = "var(--primary)",
}: {
  height?: number;
  color?: string;
}) {
  return (
    <span
      className="font-sans font-bold leading-none tracking-[-0.025em]"
      style={{ fontSize: height, color }}
    >
      {appConfig.name.toLowerCase()}
    </span>
  );
}

/**
 * Logo complet « Trombone équilibré » : le trombone fait le « e », suivi de
 * « ncrelio ». Le trombone et les lettres prennent l'encre de la marque
 * (`text-primary`), ou le blanc avec `inverted` ; `color` force une autre
 * couleur. Les deux feuilles suivent les rôles `logo-sheet-*`.
 *
 * Posé dans un lien `.group`, le trombone s'incline au survol et les
 * feuilles s'écartent (classes `enc-logo-*`, styles/motion.css).
 */
export function EncrelioLogo({
  height = 26,
  color,
  inverted = false,
  className,
}: {
  height?: number;
  color?: string;
  inverted?: boolean;
  className?: string;
}) {
  if (brandIdentity.logoUrl) {
    return (
      <img
        src={brandIdentity.logoUrl}
        alt={appConfig.name}
        style={{ height }}
        className={cn("block w-auto shrink-0", className)}
      />
    );
  }
  if (brandIdentity.customName) {
    return (
      <span
        role="img"
        aria-label={appConfig.name}
        style={color ? { color } : undefined}
        className={cn(
          "inline-flex shrink-0 items-center gap-2",
          inverted ? "text-primary-foreground" : "text-primary",
          className,
        )}
      >
        <Monogram size={height} inverted={inverted} />
        <span
          className="font-display font-semibold leading-none tracking-[-0.01em] whitespace-nowrap"
          style={{ fontSize: Math.round(height * 0.72) }}
        >
          {appConfig.name}
        </span>
      </span>
    );
  }
  return (
    <svg
      role="img"
      aria-label={appConfig.name}
      viewBox={LOCKUP_VIEWBOX}
      width={Math.round(height * LOCKUP_RATIO)}
      height={height}
      style={color ? { color } : undefined}
      className={cn(
        "enc-logo block shrink-0 overflow-visible",
        inverted ? "text-primary-foreground" : "text-primary",
        className,
      )}
    >
      <path
        data-logo="sheet-back"
        className="enc-logo-sheet-back fill-logo-sheet-back"
        d={SHEET_BACK}
      />
      <path
        data-logo="sheet-front"
        className="enc-logo-sheet-front fill-logo-sheet-front"
        d={SHEET_FRONT}
      />
      <g data-logo="ink" fill="currentColor">
        <g className="enc-logo-clip">
          <path transform={POTRACE_TRANSFORM} d={CLIP_OUTLINE} />
        </g>
        <g transform={POTRACE_TRANSFORM}>
          {LETTERS.map((d) => (
            <path key={d.slice(0, 14)} d={d} />
          ))}
        </g>
      </g>
    </svg>
  );
}
